/**
 * QiblaHeader.jsx
 * ---------------
 * Header row for the Qibla card: title on one side, mute + hint buttons
 * on the other. Also renders the hint popover as a sibling because its
 * absolute positioning is relative to this same header row.
 *
 * Purely presentational — all handlers and open/closed state are owned
 * by the parent QiblaContainer.
 */
import React from 'react';
import { Compass, Volume2, VolumeX, Info, X } from 'lucide-react';

export const QiblaHeader = ({
  title,
  muted,
  showHint,
  onToggleMute,
  onToggleHint,
  onCloseHint,
  isRTL,
  surfaceColors,
  textColors,
  labels, // { hintTitle, hintBody, muteChime, unmuteChime, helpHint }
  hintRef,
  hintExtra,
}) => (
  <div
    className="flex items-center justify-between p-4 md:p-5 relative"
    style={{ borderBottom: `1px solid ${surfaceColors.border}` }}
  >
    <div className="flex items-center gap-3">
      <Compass size={22} style={{ color: textColors.accent }} />
      <h3
        className="text-heading text-lg md:text-xl"
        style={{ color: textColors.primary }}
      >
        {title}
      </h3>
    </div>

    <div className="flex items-center gap-2">
      {/* Mute toggle */}
      <button
        type="button"
        onClick={onToggleMute}
        className="p-2 rounded-lg transition-all hover:scale-105 focus:outline-none focus:ring-2"
        style={{
          background: `${textColors.accent}12`,
          border: `1px solid ${textColors.accent}40`,
          color: textColors.primary,
        }}
        data-testid="qibla-mute-toggle"
        aria-label={muted ? labels.unmuteChime : labels.muteChime}
        title={muted ? labels.unmuteChime : labels.muteChime}
      >
        {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
      </button>

      {/* Hint toggle */}
      <button
        type="button"
        onClick={onToggleHint}
        className="p-2 rounded-lg transition-all hover:scale-105 focus:outline-none focus:ring-2"
        style={{
          background: `${textColors.accent}12`,
          border: `1px solid ${textColors.accent}40`,
          color: textColors.primary,
        }}
        data-testid="qibla-hint-toggle"
        aria-label={labels.helpHint}
        aria-expanded={showHint}
        title={labels.helpHint}
      >
        <Info size={18} />
      </button>
    </div>

    {/* Hint popover */}
    {showHint && (
      <div
        ref={hintRef}
        role="dialog"
        aria-label={labels.hintTitle}
        className="absolute z-30 top-full mt-2 w-72 sm:w-80 p-4 rounded-xl shadow-xl backdrop-blur-2xl border"
        style={{
          [isRTL ? 'left' : 'right']: 16,
          background: textColors.primary === '#FFFFFF' ? 'rgba(15, 23, 42, 0.94)' : 'rgba(248, 250, 252, 0.95)',
          borderColor: surfaceColors.border,
        }}
        data-testid="qibla-hint-popover"
      >
        <div className="flex items-start justify-between gap-3 mb-2">
          <h4
            className="text-heading text-sm"
            style={{ color: textColors.primary }}
          >
            {labels.hintTitle}
          </h4>
          <button
            type="button"
            onClick={onCloseHint}
            aria-label="close"
            className="p-1 rounded-md hover:opacity-70"
            style={{ color: textColors.secondary }}
          >
            <X size={16} />
          </button>
        </div>
        {Array.isArray(labels.hintBody) ? (
          <ul
            className="text-[13px] leading-relaxed space-y-1.5 list-none pl-0"
            style={{ color: textColors.secondary }}
            data-testid="qibla-hint-list"
          >
            {labels.hintBody.map((step, i) => (
              <li key={i} className="flex gap-2 items-start">
                <span
                  aria-hidden="true"
                  className="mt-1.5 shrink-0 rounded-full"
                  style={{
                    width: 5,
                    height: 5,
                    background: textColors.accent || textColors.secondary,
                    opacity: 0.9,
                  }}
                />
                <span className="flex-1">{step}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p
            className="text-[13px] leading-relaxed"
            style={{ color: textColors.secondary }}
          >
            {labels.hintBody}
          </p>
        )}
        {hintExtra}
      </div>
    )}
  </div>
);
